import type {OpenWorldPendingResponse,OpenWorldResponseSlot} from '@/lib/openWorlds/responses'
import type { OpenWorldCompletedAttempt, OpenWorldMessageRole } from '@/lib/openWorlds/types'
import type { PlaythroughExecutionState } from '@/lib/openWorlds/attemptPersistence'
import { validateOpenWorldNote, type OpenWorldNoteTurn } from '@/lib/openWorlds/notes'
import { isOpenWorldJournalPositiveInteger, isOpenWorldJournalUuid } from '@/lib/openWorlds/journalPersistence'
import type { ChangeOpenWorldConversationRequest, OpenWorldConversationSettings, OpenWorldDirectorNote } from '@/lib/openWorlds/conversation'

export interface OpenWorldMessageEdit {
  attemptId: string
  role: OpenWorldMessageRole
  content: string
  updatedAt: string
}

export interface OpenWorldPlayerMessage {
  id: string
  parentAttemptId: string | null
  content: string
  createdAt: string
  updatedAt: string
}

export interface OpenWorldStorySnapshot {
  responseSlots?: OpenWorldResponseSlot[]
  pendingResponse?: OpenWorldPendingResponse | null
  conversationSettings?: OpenWorldConversationSettings
  directorNotes?: OpenWorldDirectorNote[]
  displayTitle?: string | null
  openingOverride?: string | null
  playthroughState: PlaythroughExecutionState
  attempts: OpenWorldCompletedAttempt[]
  messageEdits: OpenWorldMessageEdit[]
  awaitingPlayerMessage: OpenWorldPlayerMessage | null
  noteTurns?: OpenWorldNoteTurn[]
  noteTakingPaused?: boolean
  noteResumePending?: boolean
}

export interface RewindOpenWorldStoryRequest {
  playthroughId: string
  receiptId: string
  expectedPlaythroughRevision: number
  expectedHeadId: string | null
  attemptId: string
  retainPlayerMessage?: boolean
}

export interface EditOpenWorldMessageRequest extends RewindOpenWorldStoryRequest {
  role: OpenWorldMessageRole
  content: string
}

export interface EditOpenWorldNoteRequest extends RewindOpenWorldStoryRequest {
  operation: 'edit-note'
  noteId?: string
  content: string
}

export interface SetOpenWorldNoteTakingRequest {
  playthroughId: string
  receiptId: string
  expectedPlaythroughRevision: number
  expectedHeadId: string | null
  operation: 'pause-notes' | 'resume-notes'
}

export function validNoteTakingChange(request: SetOpenWorldNoteTakingRequest): boolean {
  return validStoryMutation({ ...request, attemptId: request.playthroughId }) &&
    ['pause-notes', 'resume-notes'].includes(request.operation)
}

export function validNoteEdit(request: EditOpenWorldNoteRequest): boolean {
  if ((request.noteId !== undefined && !isOpenWorldJournalUuid(request.noteId)) || !validStoryMutation(request) || request.operation !== 'edit-note' || typeof request.content !== 'string') return false
  try { validateOpenWorldNote(request.content); return true } catch { return false }
}

export interface OpenWorldStoryMutationResponse {
  acknowledgement: {
    receiptId: string
    operation: 'pause-notes' | 'resume-notes' | 'edit-note' | 'edit' | 'rewind' | 'post-player' | 'edit-player' | 'delete-player' | ChangeOpenWorldConversationRequest['operation']
    playthroughRevision: number
    activeHeadId: string | null
  }
  snapshot: OpenWorldStorySnapshot
  replayed: boolean
}

export function validStoryMutation(request: RewindOpenWorldStoryRequest): boolean {
  return (request.retainPlayerMessage === undefined || typeof request.retainPlayerMessage === 'boolean') &&
    isOpenWorldJournalUuid(request.playthroughId) && isOpenWorldJournalUuid(request.receiptId) &&
    isOpenWorldJournalUuid(request.attemptId) &&
    (request.expectedHeadId === null || isOpenWorldJournalUuid(request.expectedHeadId)) &&
    isOpenWorldJournalPositiveInteger(request.expectedPlaythroughRevision)
}

export function validMessageEdit(request: EditOpenWorldMessageRequest): boolean {
  return validStoryMutation(request) && ['player', 'storyteller'].includes(request.role) &&
    typeof request.content === 'string' && Boolean(request.content.trim()) &&
    new TextEncoder().encode(request.content).length <= (request.role === 'player' ? 65_536 : 262_144)
}

export function editedOpenWorldMessage(
  attempt: OpenWorldCompletedAttempt, role: OpenWorldMessageRole, edits: readonly OpenWorldMessageEdit[],
): string {
  return edits.find((edit) => edit.attemptId === attempt.id && edit.role === role)?.content ??
    (role === 'player' ? attempt.userMessage : attempt.storytellerMessage)
}

export interface ChangeOpenWorldPlayerMessageRequest {
  playthroughId: string
  receiptId: string
  expectedPlaythroughRevision: number
  expectedHeadId: string | null
  playerMessageId: string
  operation: 'post-player' | 'edit-player' | 'delete-player'
  content?: string
}

export type OpenWorldStoryChangeRequest = EditOpenWorldMessageRequest | RewindOpenWorldStoryRequest |
  ChangeOpenWorldPlayerMessageRequest | EditOpenWorldNoteRequest | SetOpenWorldNoteTakingRequest | ChangeOpenWorldConversationRequest

export function validPlayerMessageChange(request: ChangeOpenWorldPlayerMessageRequest): boolean {
  return validStoryMutation({ ...request, attemptId: request.playerMessageId }) &&
    ['post-player', 'edit-player', 'delete-player'].includes(request.operation) &&
    (request.operation === 'delete-player' ? request.content === undefined :
      typeof request.content === 'string' && Boolean(request.content.trim()) &&
      new TextEncoder().encode(request.content).length <= 65_536)
}
